import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Info,
  Mail,
  Monitor,
  Shield,
  Smartphone,
  Users,
  Wifi,
  XCircle,
  Zap,
  Phone,
} from "lucide-react";

function Support() {
  const [activeTab, setActiveTab] = useState("login");

  const loginSteps = [
    {
      step: 1,
      title: "افتح متصفح الويب",
      description: "استخدم أي متصفح حديث مثل Chrome أو Edge أو Firefox",
      icon: Monitor,
      color: "text-primary",
      bgColor: "bg-primary/5",
    },
    {
      step: 2,
      title: "اذهب إلى بوابة الموظفين",
      description: "اكتب الرابط التالي في شريط العنوان: portal.wsmco.sa",
      icon: Wifi,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      step: 3,
      title: "اضغط على تسجيل الدخول",
      description: "ستجد زر تسجيل الدخول في أعلى الصفحة",
      icon: Zap,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      step: 4,
      title: "أدخل بريدك الإلكتروني",
      description: "اكتب بريدك الإلكتروني: yourname@wsmco.sa",
      icon: Mail,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      step: 5,
      title: "أدخل كلمة المرور",
      description: "اكتب كلمة المرور الخاصة بحساب Office 365",
      icon: Shield,
      color: "text-red-600",
      bgColor: "bg-red-50",
    },
    {
      step: 6,
      title: "اضغط تسجيل الدخول",
      description: "ستتم إعادة توجيهك إلى البوابة بعد التحقق من بياناتك",
      icon: CheckCircle,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
  ];

  const commonIssues = [
    {
      issue: "نسيت كلمة المرور",
      solution:
        "اتصل بفريق المنتجات على الرقم 920000000 أو أرسل بريد إلكتروني إلى IT@wsmco.sa",
      severity: "high",
    },
    {
      issue: "لا يمكنني الوصول إلى البريد الإلكتروني",
      solution: "تأكد من أن حسابك مفعل وأن لديك صلاحيات الوصول المناسبة",
      severity: "medium",
    },
    {
      issue: "مشكلة في تسجيل الدخول من المنزل",
      solution: "تأكد من استخدام VPN الشركة أو الاتصال بشبكة آمنة",
      severity: "medium",
    },
    {
      issue: "رسالة خطأ في المصادقة",
      solution: "تأكد من إدخال البريد الإلكتروني الصحيح مع @wsmco.sa",
      severity: "low",
    },
  ];

  const contactInfo = [
    {
      department: "فريق المنتجات",
      phone: "920000000",
      email: "IT@wsmco.sa",
      hours: "الأحد - الخميس: 8:00 ص - 5:00 م",
      icon: Users,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary/5 to-primary/10 p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center space-y-4">
          {/* Back to Home Link */}
          <div className="flex justify-center mb-4">
            <a
              href="/"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:text-primary-dark bg-primary/5 hover:bg-primary/10 rounded-lg transition-colors duration-200 hover:shadow-sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              العودة للصفحة الرئيسية
            </a>
          </div>

          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-primary rounded-full shadow-lg">
            <HelpCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900">
            مركز الدعم والمساعدة
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            دليل شامل لمساعدتك في تسجيل الدخول إلى بوابة الموظفين باستخدام حساب
            Office 365
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex justify-center space-x-2 rtl:space-x-reverse">
          {[
            { id: "login", label: "دليل تسجيل الدخول", icon: CheckCircle },
            { id: "issues", label: "المشاكل الشائعة", icon: AlertCircle },
            { id: "contact", label: "معلومات التواصل", icon: Users },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => setActiveTab(tab.id)}
              className="flex items-center gap-2"
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Content Sections */}
        {activeTab === "login" && (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold text-gray-900">
                  دليل تسجيل الدخول خطوة بخطوة
                </CardTitle>
                <CardDescription className="text-lg text-gray-600">
                  اتبع هذه الخطوات البسيطة لتسجيل الدخول إلى بوابة الموظفين
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {loginSteps.map((step) => (
                    <div
                      key={step.step}
                      className="relative p-6 bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 group"
                    >
                      <div
                        className={`inline-flex items-center justify-center w-12 h-12 ${step.bgColor} rounded-lg mb-4 group-hover:scale-110 transition-transform duration-200`}
                      >
                        <step.icon className={`w-6 h-6 ${step.color}`} />
                      </div>
                      <Badge
                        variant="secondary"
                        className="absolute top-4 left-4"
                      >
                        الخطوة {step.step}
                      </Badge>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Important Notes */}
            <Card className="border-0 shadow-lg bg-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-primary-dark">
                  <Info className="w-5 h-5" />
                  ملاحظات مهمة
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-primary-dark">
                    تأكد من أن بريدك الإلكتروني ينتهي بـ{" "}
                    <strong>@wsmco.sa</strong>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-primary-dark">
                    استخدم كلمة المرور الخاصة بحساب Office 365 وليس كلمة مرور
                    أخرى
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-primary-dark">
                    إذا واجهت مشاكل، لا تتردد في التواصل مع فريق تكنولوجيا
                    المعلومات
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "issues" && (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  المشاكل الشائعة وحلولها
                </CardTitle>
                <CardDescription className="text-lg text-gray-600">
                  إليك حلول لأكثر المشاكل شيوعاً عند تسجيل الدخول
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {commonIssues.map((item, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-right hover:text-primary">
                        <div className="flex items-center gap-3">
                          <Badge
                            variant={
                              item.severity === "high"
                                ? "destructive"
                                : item.severity === "medium"
                                ? "secondary"
                                : "outline"
                            }
                          >
                            {item.severity === "high"
                              ? "عالية"
                              : item.severity === "medium"
                              ? "متوسطة"
                              : "منخفضة"}
                          </Badge>
                          <span className="font-medium">{item.issue}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-right">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <p className="text-gray-700 leading-relaxed">
                            {item.solution}
                          </p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>

            {/* Troubleshooting Tips */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <Zap className="w-5 h-5" />
                  نصائح استكشاف الأخطاء
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-white/50 rounded-lg">
                    <Smartphone className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-amber-900">
                        تأكد من المتصفح
                      </h4>
                      <p className="text-sm text-amber-800">
                        استخدم أحدث إصدار من المتصفح
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/50 rounded-lg">
                    <Wifi className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-amber-900">
                        تحقق من الاتصال
                      </h4>
                      <p className="text-sm text-amber-800">
                        تأكد من استقرار الإنترنت
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/50 rounded-lg">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-amber-900">
                        امسح الكوكيز
                      </h4>
                      <p className="text-sm text-amber-800">
                        احذف بيانات التصفح المخزنة
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-white/50 rounded-lg">
                    <Monitor className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-amber-900">
                        جرب متصفح آخر
                      </h4>
                      <p className="text-sm text-amber-800">
                        أو جرب وضع التصفح الخاص
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "contact" && (
          <div className="space-y-6">
            <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">
                  معلومات التواصل والدعم
                </CardTitle>
                <CardDescription className="text-lg text-gray-600">
                  نحن هنا لمساعدتك! تواصل معنا في أي وقت تحتاج فيه للمساعدة
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6">
                  {contactInfo.map((contact, index) => (
                    <div
                      key={index}
                      className="p-6 bg-gradient-to-br from-primary/5 to-primary/5 rounded-xl border border-primary/20 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <contact.icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-primary-dark">
                            {contact.department}
                          </h3>
                          <p className="text-sm text-primary-dark">
                            {contact.hours}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-primary" />
                          <a
                            href={`mailto:${contact.email}`}
                            className="text-primary-dark hover:text-primary-dark underline"
                          >
                            {contact.email}
                          </a>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-primary" />
                          <a
                            href={`tel:${contact.phone}`}
                            className="text-primary-dark hover:text-primary-dark underline"
                          >
                            {contact.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-8">
          <Separator className="mb-6" />
          <p className="text-gray-500">
            © 2025 شركة شركة وسم لتقنية الأعمال. جميع الحقوق محفوظة.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            تم تطوير هذه البوابة لخدمة موظفي الشركة وتسهيل وصولهم للخدمات
          </p>
        </div>
      </div>
    </div>
  );
}

export default Support;
